import React, { useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Loader2, X, FileText, Image as ImageIcon, Video, Film,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_BYTES = 50 * 1024 * 1024;

const _fmt = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  for (const u of units) {
    if (v < 1024 || u === 'GB') return `${u === 'B' ? v : v.toFixed(1)} ${u}`;
    v /= 1024;
  }
  return `${v.toFixed(1)} GB`;
};

const KIND_ICON = {
  image:    ImageIcon,
  video:    Video,
  pdf:      FileText,
  document: FileText,
  other:    Film,
};

/**
 * FileUploader — reusable drag-drop + click uploader for the DCCF Media
 * Library. Single or multiple files, 50 MB cap enforced client-side then
 * re-enforced by the backend.
 *
 * Props
 *   onUploaded(doc)   fires per successful file
 *   onAllDone()       fires once after the full batch resolves
 *   context           optional { type, id, alt_text, tags } passed with every upload
 *   accept            comma-separated MIME list; defaults to all allowed
 *   multiple          allow multi-file (default true)
 *   compact           show a slim pill instead of the full drop zone
 */
export const FileUploader = ({
  onUploaded,
  onAllDone,
  context,
  accept,
  multiple = true,
  compact = false,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});   // name -> 0..100
  const [dragging, setDragging] = useState(false);

  const pickFiles = () => inputRef.current?.click();

  const uploadOne = useCallback(async (file) => {
    if (file.size > MAX_BYTES) {
      toast.error(`${file.name} is larger than ${_fmt(MAX_BYTES)}`);
      return null;
    }
    const form = new FormData();
    form.append('file', file);
    if (context?.type) form.append('context_type', context.type);
    if (context?.id)   form.append('context_id',   context.id);
    if (context?.alt_text) form.append('alt_text', context.alt_text);
    if (context?.tags && context.tags.length) {
      form.append('tags', context.tags.join(','));
    }
    try {
      const { data } = await axios.post(`${API}/admin/media/upload`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (!e.total) return;
          setProgress((p) => ({ ...p, [file.name]: Math.round((e.loaded / e.total) * 100) }));
        },
        timeout: 180000,
      });
      onUploaded?.(data);
      return data;
    } catch (err) {
      const msg = err?.response?.data?.detail || `Upload failed for ${file.name}`;
      toast.error(msg);
      return null;
    }
  }, [context, onUploaded]);

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        await uploadOne(f);
      }
      onAllDone?.();
    } finally {
      setUploading(false);
      setProgress({});
    }
  }, [uploadOne, onAllDone]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    handleFiles(files);
  };

  if (compact) {
    return (
      <>
        <input
          ref={inputRef}
          type="file"
          accept={accept || undefined}
          multiple={multiple}
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
          className="hidden"
          data-testid="media-file-input"
        />
        <button
          type="button"
          onClick={pickFiles}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#002249] text-white text-[12.5px] font-semibold hover:bg-[#0a3570] disabled:opacity-60"
          data-testid="media-upload-trigger"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </>
    );
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={accept || undefined}
        multiple={multiple}
        onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        className="hidden"
        data-testid="media-file-input"
      />
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={pickFiles}
        className={`rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
          dragging
            ? 'border-[#002249] bg-[#002249]/5'
            : 'border-slate-200 hover:border-slate-300 bg-slate-50/60 hover:bg-slate-50'
        }`}
        data-testid="media-dropzone"
      >
        <div className="w-10 h-10 mx-auto rounded-xl bg-white border border-slate-200 flex items-center justify-center mb-2">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin text-[#002249]" /> : <Upload className="w-4 h-4 text-[#002249]" />}
        </div>
        <div className="font-semibold text-[14px] text-[#1a1a1a]">
          {uploading ? 'Uploading…' : 'Drop files or click to upload'}
        </div>
        <div className="text-[11.5px] text-slate-500 mt-1">
          Images · PDFs · Office docs · Short videos — up to {_fmt(MAX_BYTES)}
        </div>
      </div>

      {Object.keys(progress).length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="media-upload-progress">
          {Object.entries(progress).map(([name, pct]) => (
            <div key={name} className="text-[11px] text-slate-600">
              <div className="flex items-center justify-between">
                <span className="truncate max-w-[220px]">{name}</span>
                <span className="font-mono">{pct}%</span>
              </div>
              <div className="h-1 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                <div
                  className="h-full bg-[#002249] transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const FileKindIcon = ({ kind, className = 'w-4 h-4' }) => {
  const Icon = KIND_ICON[kind] || Film;
  return <Icon className={className} />;
};

export const formatBytes = _fmt;

export default FileUploader;
